import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuIcon from '@material-ui/icons/Menu';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core';
import DarkModeButton from '../DarkModeButton';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../Logo';
import LanguageSelector from '../../Language/LanguageSelector';
import { LanguageContext } from '../../Language/LanguageProvider';

const drawerWidth = 200;
const menu = { home: 0, myWork: 1, contact: 2 };

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    background: theme.palette.primary.main,
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
    // @ts-ignore
    backgroundColor: theme.palette.secondary.main,
  },
  navigationMenu: {
    height: '60%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  content: {
    flexGrow: 1,
    height: '100vh',
    background: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(3),
  },

  listIconContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  listItem: {
    '&.MuiListItem-root': {
      color: 'white',
      '&:hover': {
        opacity: 0.7,
      },
    },
    '&.Mui-selected': {
      color: `${theme.palette.common.myViolet}`,
    },
  },
  spaceShipListContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 'auto',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  spaceShipItem: {
    '&.MuiListItem-root': {
      color: 'white',
      '&:hover': {
        opacity: 0.7,
      },

      '&.Mui-selected': {
        color: `${theme.palette.secondary.main}`,
        background: 'transparent',
      },
      '& i': {
        fontSize: '1.5em',
      },
    },
  },
  text: {
    ...theme.typography.link,
    textAlign: 'center',
    color: 'inherit',
  },
}));

const LateralDrawer = props => {
  const { window } = props;
  const classes = useStyles();
  const location = useLocation();
  const theme = useTheme();
  const { dictionary } = useContext(LanguageContext);
  const [selectedMenu, setSelectedMenu] = useState(menu.home);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    switch (location.pathname) {
      case '/mywork':
        setSelectedMenu(menu.myWork);
        break;
      case '/contact':
        setSelectedMenu(menu.contact);
        break;
      default:
        setSelectedMenu(menu.home);
    }
  }, [selectedMenu]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <div className={classes.toolbar} />
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Logo />
      </Link>
      <List className={classes.navigationMenu}>
        <ListItem
          classes={{
            selected: classes.listItem,
            root: classes.listItem,
          }}
          component={Link}
          onClick={() => setSelectedMenu(menu.home)}
          to="/"
          selected={selectedMenu === menu.home}
        >
          <ListItemText>
            <Typography className={classes.text}>
              {dictionary['header-init']}
            </Typography>
          </ListItemText>
        </ListItem>
        <ListItem
          classes={{ selected: classes.listItem, root: classes.listItem }}
          selected={selectedMenu === menu.myWork}
          onClick={() => setSelectedMenu(menu.myWork)}
          component={Link}
          to="/mywork"
        >
          <ListItemText>
            <Typography className={classes.text}>
              {dictionary['header-portfolio']}
            </Typography>
          </ListItemText>
        </ListItem>
        <ListItem
          classes={{ selected: classes.listItem, root: classes.listItem }}
          selected={selectedMenu === menu.contact}
          onClick={() => setSelectedMenu(menu.contact)}
          component={Link}
          to="/contact"
        >
          <ListItemText>
            <Typography className={classes.text}>
              {dictionary['header-contact']}
            </Typography>
          </ListItemText>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItemIcon className={classes.listIconContainer}>
          <DarkModeButton />
        </ListItemIcon>
        <ListItem>
          <LanguageSelector />
        </ListItem>
      </List>
    </div>
  );

  const SpaceShipNav = (
    <>
      <List className={classes.spaceShipListContainer}>
        <ListItem
          classes={{
            selected: classes.spaceShipItem,
            root: classes.spaceShipItem,
          }}
          component={Link}
          onClick={() => setSelectedMenu(menu.home)}
          to="/"
          selected={selectedMenu === menu.home}
        >
          <i className=" fas fa-rocket"></i>
        </ListItem>
        <ListItem
          classes={{
            selected: classes.spaceShipItem,
            root: classes.spaceShipItem,
          }}
          selected={selectedMenu === menu.myWork}
          onClick={() => setSelectedMenu(menu.myWork)}
          component={Link}
          to="/mywork"
        >
          <i className=" fas fa-rocket"></i>
        </ListItem>
        <ListItem
          classes={{
            selected: classes.spaceShipItem,
            root: classes.spaceShipItem,
          }}
          selected={selectedMenu === menu.contact}
          onClick={() => setSelectedMenu(menu.contact)}
          component={Link}
          to="/contact"
        >
          <i className=" fas fa-rocket"></i>
        </ListItem>
      </List>
    </>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" elevation={0} className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          {SpaceShipNav}
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer} aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden smUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <div className={classes.toolbar}></div>
    </div>
  );
};

LateralDrawer.propTypes = {
  window: PropTypes.func,
};

export default LateralDrawer;
